import React, { useCallback, useEffect, useState } from "react";
import "../../App.css";
import "../../profile.css";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import endpoints from "../../services/endpoints";
import service from "../../services/service";
import {
  GoogleMap,
  Polyline,
  Marker,
  OverlayView,
  useJsApiLoader,
} from "@react-google-maps/api";
import polyline from "@mapbox/polyline";

const decodePolyline = (encoded) => {
  return polyline.decode(encoded).map(([lat, lng]) => ({
    lat,
    lng,
  }));
};

const ChallengeManagementDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [challengeDetail, setChallengeDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  // Challenge Action States
  const [showStartSuccessModal, setShowStartSuccessModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [activeChallengeName, setActiveChallengeName] = useState("");
  const [loadingAction, setLoadingAction] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  // --- API CALL ---
  const fetchChallenge = useCallback(async () => {
    try {
      setLoading(true);
      const response = await service.get(
        endpoints.challengeOrders.myChallengeDetails(id)
      );
      setChallengeDetail(response?.data?.data || null);
    } catch (error) {
      console.log("API Error:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchChallenge();
  }, [id, fetchChallenge]);

  // --- SAFE DERIVED VALUES ---
  const routeCoords = challengeDetail?.routePolyline
    ? decodePolyline(challengeDetail.routePolyline)
    : [];

  const mapCenter =
    routeCoords.length > 0
      ? routeCoords[0]
      : {
          lat: challengeDetail?.startPointLatitude || 28.6139,
          lng: challengeDetail?.startPointLongitude || 77.209,
        };

  const onMapLoad = useCallback(
    (map) => {
      if (routeCoords.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        routeCoords.forEach((p) => bounds.extend(p));
        map.fitBounds(bounds);
      }
    },
    [routeCoords]
  );

  const hours = Math.floor((challengeDetail?.durationInMin || 0) / 60);
  const minutes = (challengeDetail?.durationInMin || 0) % 60;

  // --- ACTIONS ---
  const handleStartChallenge = async () => {
    try {
      setLoadingAction(true);

      await service.patch(
        endpoints.profile.startChallenge(challengeDetail.challengeId)
      );

      setShowStartSuccessModal(true);
    } catch (error) {
      console.log(error);

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        "";

      if (
        message.toLowerCase().includes("already") ||
        message.toLowerCase().includes("progress")
      ) {
        setActiveChallengeName(
          error?.response?.data?.data?.challengeName || "Active Challenge"
        );
        setShowPauseModal(true);
      }
    } finally {
      setLoadingAction(false);
    }
  };

  const handlePauseCurrent = async () => {
    try {
      setLoadingAction(true);

      
      await service.patch(endpoints.profile.pauseCurrent);
      setShowPauseModal(false);

     
      await service.patch(
        endpoints.profile.startChallenge(challengeDetail.challengeId)
      );
      setShowStartSuccessModal(true);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingAction(false);
    }
  };

  if (loading) {
    return (
      <div className="team-details-page">
        <h3>Loading Challenge...</h3>
      </div>
    );
  }

  if (!challengeDetail) {
    return (
      <div className="team-details-page">
        <h3>Challenge data not found</h3>
      </div>
    );
  }

  return (
    <div className="team-details-page">
      {/* HEADER */}
      <div className="details-header">
        <div className="details-left">
          <ArrowLeft className="cursor" onClick={() => navigate(-1)} />
          <h2>Challenge Details</h2>
        </div>
      </div>

      {/* TOP CARD */}
      <div className="team-info-card">
        <div className="team-card-left">
          <div className="team-logo">
            <img src="/challenge_icon.svg" alt="challenge icon" />
          </div>

          <div>
            <h3>{challengeDetail.challengeName}</h3>
            <p>{challengeDetail.challengeType}</p>
          </div>
        </div>

        <div className="team-btns">
          <button
            className="red-btn"
            onClick={handleStartChallenge}
            disabled={loadingAction}
          >
            {loadingAction ? "Please wait..." : "Start Challenge"}
          </button>
        </div>
      </div>

      {/* MAP */}
      <div
        className="map-box"
        style={{
          height: "350px",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={mapCenter}
            zoom={7}
            onLoad={onMapLoad}
            options={{
              streetViewControl: true,
              mapTypeControl: true,
              fullscreenControl: true,
            }}
          >
            {/* Route */}
            {routeCoords.length > 1 && (
              <Polyline
                path={routeCoords}
                options={{
                  strokeColor: "#ff4444",
                  strokeWeight: 5,
                }}
              />
            )}

            {/* START */}
            <Marker
              position={{
                lat: challengeDetail.startPointLatitude,
                lng: challengeDetail.startPointLongitude,
              }}
            />
            <OverlayView
              position={{
                lat: challengeDetail.startPointLatitude,
                lng: challengeDetail.startPointLongitude,
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  transform: "translate(-50%, -65px)",
                  background: "#fff",
                  border: "2px solid #ef4444",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#222",
                  boxShadow: "0 3px 10px rgba(0,0,0,.25)",
                  width: "100px",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {challengeDetail.startPoint}
              </div>
            </OverlayView>

            {/* MILESTONES */}
            {challengeDetail?.milestoneDetails?.map((milestone) => (
              <React.Fragment key={milestone.id}>
                <Marker
                  position={{
                    lat: milestone.latitude,
                    lng: milestone.longitude,
                  }}
                />
                <OverlayView
                  position={{
                    lat: milestone.latitude,
                    lng: milestone.longitude,
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    style={{
                      transform: "translate(-50%, -65px)",
                      background: "#fff",
                      border: "2px solid #ef4444",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#222",
                      boxShadow: "0 3px 10px rgba(0,0,0,.25)",
                      width: "100px",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {milestone.title}
                  </div>
                </OverlayView>
              </React.Fragment>
            ))}

            {/* END */}
            <Marker
              position={{
                lat: challengeDetail.endPointLatitude,
                lng: challengeDetail.endPointLongitude,
              }}
            />
            <OverlayView
              position={{
                lat: challengeDetail.endPointLatitude,
                lng: challengeDetail.endPointLongitude,
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  transform: "translate(-50%, -65px)",
                  background: "#fff",
                  border: "2px solid #ef4444",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#222",
                  boxShadow: "0 3px 10px rgba(0,0,0,.25)",
                  width: "100px",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {challengeDetail.endPoint}
              </div>
            </OverlayView>
          </GoogleMap>
        )}
      </div>

      {/* STATS */}
      <div className="stats-row">
        <div>
          <span>Distance</span>
          <h4>{challengeDetail.totalDistance} KM</h4>
        </div>
        <div>
          <span>Milestones</span>
          <h4>{challengeDetail.totalMilestoneCount}</h4>
        </div>
        <div>
          <span>Duration</span>
          <h4>
            {hours}H {minutes}M
          </h4>
        </div>
        <div>
          <span>Type</span>
          <h4>{challengeDetail.challengeType}</h4>
        </div>
      </div>

      {/* OVERVIEW */}
      <div className="team-section">
        <h3>Mission Overview</h3>
        <p>{challengeDetail.challengeOverview}</p>
      </div>

      {/* MILESTONES */}
      <div className="team-section">
        <h3>Milestones</h3>
        <div className="team-benefit-grid">
          {challengeDetail?.milestoneDetails?.map((m) => (
            <div key={m.id} className="team-reward-card">
              <span className="team-flag">📍</span>
              <h4>{m.title}</h4>
              <p>{m.targetDistance} KM</p>
            </div>
          ))}
        </div>
      </div>

      {/* START SUCCESS MODAL */}
      {showStartSuccessModal && (
        <div className="challenge-modal-overlay">
          <div className="challenge-success-modal">
            <button
              className="challenge-modal-close"
              onClick={() => setShowStartSuccessModal(false)}
            >
              ✕
            </button>
            <div className="challenge-success-icon">🚀</div>
            <h2>Challenge started!</h2>
            <p>Stay strong, stay focused — every step counts.</p>
            <div className="challenge-name-pill">
              🏆 {challengeDetail.challengeName}
            </div>
            <button
              className="challenge-success-btn"
              onClick={() => setShowStartSuccessModal(false)}
            >
              ✓ Okay, thank you!
            </button>
          </div>
        </div>
      )}

      {/* PAUSE MODAL */}
      {showPauseModal && (
        <div className="challenge-modal-overlay">
          <div className="challenge-pause-modal">
            <div className="warning-circle">⚠</div>
            <h3>
              One challenge is already in progress. Please pause it first.
            </h3>
            <div className="active-challenge-pill">
              🏆 {activeChallengeName}
            </div>
            <div className="challenge-modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowPauseModal(false)}
              >
                Cancel
              </button>
              <button className="pause-btn" onClick={handlePauseCurrent}>
                Pause active
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChallengeManagementDetails;